'use client';

import { useState } from 'react';

import type { LocalCityRichTextSegment } from './LocalCityLandingPage';
import styles from './LocalCityLandingPage.module.css';

export interface LocalCityAccordionItem {
  id: string;
  title: string;
  /** Panel paragraphs — one segment array per source <p>. */
  paragraphs: LocalCityRichTextSegment[][];
}

function SegmentText({ segments }: { segments: LocalCityRichTextSegment[] }) {
  return (
    <>
      {segments.map((segment, index) => {
        const key = `${segment.type}-${index}`;

        if (segment.type === 'link') {
          return (
            <a key={key} href={segment.href}>
              {segment.text}
            </a>
          );
        }

        if (segment.type === 'hiddenText') {
          return (
            <span key={key} hidden data-cms-hidden-text>
              {segment.text}
            </span>
          );
        }

        return <span key={key}>{segment.text}</span>;
      })}
    </>
  );
}

/**
 * Typed rebuild of the Magento `.ip-camera-accordion-*` jQuery accordion
 * (source hides all panels on load and slide-toggles per click). Panels stay
 * in the DOM when closed (`hidden`) so content parity sees the hidden text.
 */
export function LocalCityAccordion({ blockId, items }: { blockId: string; items: LocalCityAccordionItem[] }) {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className={styles.accordionWrapper} data-cms-block-id={blockId}>
      {items.map((item) => {
        const open = openIds.has(item.id);
        const buttonId = `${item.id}-button`;
        const panelId = `${item.id}-panel`;
        return (
          <div key={item.id} className={styles.accordionSingle}>
            <button
              id={buttonId}
              type="button"
              className={`${styles.accordionBtn}${open ? ` ${styles.accordionBtnActive}` : ''}`}
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggle(item.id)}
            >
              {item.title}
            </button>
            <div
              id={panelId}
              className={styles.accordionContent}
              role="region"
              aria-labelledby={buttonId}
              hidden={!open}
            >
              {item.paragraphs.map((paragraph, index) => (
                <p key={`${item.id}-p-${index}`}>
                  <SegmentText segments={paragraph} />
                </p>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
